import { PageShell, LegalSection } from "./PageShell";

export default function PrivacyPage() {
  return (
    <PageShell eyebrow="841 wellness" title="Privacy Policy">
      <p className="text-xs text-[#8A8079]" data-testid="privacy-updated">
        Effective date: July 2026
      </p>

      <LegalSection title="Overview" testId="privacy-section-overview">
        <p>
          841 Wellness is a personalised wellness platform for women. Because the app may process
          personal wellness information, this policy explains what information may be collected, why
          it is collected, how it is used, when it is shared, and what rights you have.
        </p>
        <p>
          We want this to be easy to read. If anything is unclear, write to us at{" "}
          <strong>privacy@the841.com</strong>.
        </p>
      </LegalSection>

      <LegalSection title="Information that may be collected" testId="privacy-section-collect">
        <p>Depending on the features you choose to use, 841 may collect:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Name and account information</li>
          <li>Email address</li>
          <li>Age/date of birth, height, weight and wellness goals</li>
          <li>Fitness goals and workout preferences</li>
          <li>Dietary preferences such as vegetarian, vegan or non-vegetarian</li>
          <li>Food allergies or intolerances</li>
          <li>Relevant wellness conditions or considerations you choose to provide</li>
          <li>Menstrual-cycle information and cycle-related logs</li>
          <li>Mood and emotional-wellbeing information</li>
          <li>Sleep and recovery information</li>
          <li>Workout and activity information</li>
          <li>Hydration and other wellness information</li>
          <li>Journal entries, reflections and intentions</li>
          <li>Voice/audio input when you choose to use voice-based features</li>
          <li>
            Information from connected health or wearable platforms such as WHOOP or Apple Health —
            only after you grant permission
          </li>
          <li>Payment and subscription information, processed by the applicable payment provider</li>
        </ul>
      </LegalSection>

      <LegalSection title="How the information is used" testId="privacy-section-use">
        <p>The information is used to:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Personalise workouts and wellness recommendations</li>
          <li>Adapt recommendations based on your goals, preferences and available information</li>
          <li>Provide cycle-aware wellness guidance where applicable</li>
          <li>Provide nutrition guidance and meal suggestions</li>
          <li>Provide Balance features such as meditation, breathing and mind-body sessions</li>
          <li>Provide Self/Reflect features and AI-assisted reflection</li>
          <li>Allow the AI wellness coach to provide contextually relevant responses</li>
          <li>Analyse wellness patterns and provide insights</li>
          <li>Operate, maintain and improve the service</li>
          <li>Process subscriptions and payments</li>
          <li>Communicate important service information to you</li>
        </ul>
        <p>
          Your personal information is not used to sell advertising, and we do not sell it to third
          parties.
        </p>
      </LegalSection>

      <LegalSection title="Wearable and health integrations" testId="privacy-section-wearables">
        <p>If you connect WHOOP, Apple Health or another supported health platform:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>You must explicitly grant permission before any data is accessed.</li>
          <li>Only the data you permit is accessed.</li>
          <li>The information is used to personalise your own wellness experience.</li>
          <li>It is not used for advertising and is not sold to third parties.</li>
          <li>
            You can disconnect an integration at any time through the applicable settings or
            platform controls.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="AI and voice features" testId="privacy-section-ai">
        <p>
          841 may use third-party AI and speech-processing services to provide features such as AI
          wellness coaching, voice conversations, speech-to-text, text-to-speech, and personalised
          analysis and recommendations. Information sent to these providers is processed only as
          necessary to provide the relevant feature.
        </p>
        <p>When you use voice input, it helps to know the difference between:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>
            <strong>Audio/voice input</strong> — the recording of your voice, captured only when you
            choose to speak.
          </li>
          <li>
            <strong>Transcribed text</strong> — the text version of what you said, created by
            speech-to-text processing.
          </li>
          <li>
            <strong>AI processing</strong> — the analysis of your input so the coach can respond in
            a way that is relevant to you.
          </li>
        </ul>
        <p>
          Where original recordings are retained, this will be made clear in the app; where they are
          not needed beyond providing the feature, they are not kept. Your conversations and journal
          reflections are not used to train third-party AI models.
        </p>
      </LegalSection>

      <LegalSection title="Journal and reflection data" testId="privacy-section-journal">
        <p>
          Journal entries, reflections and intentions are your personal content. They may be stored
          and processed so that 841 can provide the functionality you ask for and maintain relevant
          context for your experience.
        </p>
        <p>
          You can request deletion of your personal data and content at any time, subject to
          legitimate legal or operational requirements.
        </p>
      </LegalSection>

      <LegalSection title="Data sharing" testId="privacy-section-sharing">
        <p>
          841 may share information with service providers only where necessary to operate the
          service, such as:
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Cloud and database infrastructure</li>
          <li>Authentication providers</li>
          <li>Payment providers</li>
          <li>AI and speech-processing providers</li>
          <li>Analytics or technical service providers, where actually implemented</li>
        </ul>
        <p>
          841 does not sell your personal data. Beyond these limited circumstances, your information
          is not shared.
        </p>
      </LegalSection>

      <LegalSection title="Data retention and deletion" testId="privacy-section-retention">
        <p>
          We keep your information for as long as your account is active or as needed to provide the
          service. Some information may be retained for a limited period where required for
          necessary business or legal purposes; where specific retention periods apply, they will be
          described in the app.
        </p>
        <p>
          You can request deletion of your account and associated personal information at any time
          by writing to <strong>privacy@the841.com</strong>, subject to legitimate legal
          requirements or information that must be retained for necessary business or legal
          purposes.
        </p>
      </LegalSection>

      <LegalSection title="Security" testId="privacy-section-security">
        <p>
          We use reasonable technical and organisational measures to protect your personal
          information. No method of storage or transmission is completely secure, so we cannot
          promise absolute security — but protecting your information is something we take
          seriously and continually work on.
        </p>
      </LegalSection>

      <LegalSection title="Children's privacy" testId="privacy-section-children">
        <p>
          841 is intended for users aged 16 and above and is not directed to children under 16. If
          we learn that we have collected information from someone under 16, we will delete it.
        </p>
      </LegalSection>

      <LegalSection title="This website and the waitlist" testId="privacy-section-waitlist">
        <p>
          On this website, joining the waitlist collects your email address and, if you choose to
          share it, your name. We use this only to contact you about the launch of 841. Support
          messages sent through this website collect your name, email and message so we can respond
          to you.
        </p>
      </LegalSection>

      <LegalSection title="Contact and your rights" testId="privacy-section-contact">
        <p>
          For privacy questions, or to request access to, correction of, or deletion of your
          personal information, contact us at <strong>privacy@the841.com</strong>. We will respond
          as promptly as we can.
        </p>
        <p>
          If we update this policy, the revised version will be posted on this page with a new
          effective date.
        </p>
      </LegalSection>
    </PageShell>
  );
}
